import { timeAsync } from '@ifixit/helpers';
import { _js } from '@ifixit/localize';
import { GraphQLClient, gql } from 'graphql-request';
import * as Sentry from '@sentry/browser';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { parse } from 'graphql';
import {
   getCustomerAccessToken,
   shopifyApiClient,
   type ShopifyStorefront,
} from 'Shared/shopify/storefront';

export type ShopifyOrder = {
   totalPriceV2: {
      amount: number;
      currencyCode: string;
   };
   statusUrl: string;
   fulfillmentStatus: string;
   name: string;
   id: string;
   orderNumber: number;
   successfulFulfillments: {
      trackingCompany: string;
      trackingInfo: {
         number: string;
         url: string;
      }[];
   }[];
   processedAt: string;
};

type GetCustomerOrdersProps = {
   storeDomain: string;
   storefrontAccessToken: string;
   apiVersion: string;
   multipassToken: string;
};

async function getCustomerOrders({
   storeDomain,
   storefrontAccessToken,
   apiVersion,
   multipassToken,
}: GetCustomerOrdersProps): Promise<ShopifyOrder[]> {
   const graphQLClient = shopifyApiClient({
      storeDomain,
      storefrontAccessToken,
      version: apiVersion,
   });
   const customerAccessToken = await getCustomerAccessToken(graphQLClient, multipassToken);
   return fetchOrders(graphQLClient, customerAccessToken);
}

export async function getCustomerOrdersFromAllStores(
   storefronts: ShopifyStorefront[]
): Promise<ShopifyOrder[]> {
   const ordersFromAllStores = await timeAsync('shopify.get.order-history-orders', () =>
      Promise.all(
         storefronts.map(storefront =>
            getCustomerOrders(storefront).catch(error => {
               console.error(`Error fetching orders from store ${storefront.storeDomain}:`, error);
               Sentry.captureException(error, {
                  extra: { storeDomain: storefront.storeDomain },
               });
               return [] as ShopifyOrder[];
            })
         )
      )
   );
   return ordersFromAllStores.flat();
}

type ShopifyOrdersData = {
   customer: {
      orders: {
         nodes: ShopifyOrder[];
      };
   };
};

async function fetchOrders(graphQLClient: GraphQLClient, customerAccessToken: string | null) {
   if (!customerAccessToken) {
      return [];
   }
   const query: TypedDocumentNode<ShopifyOrdersData, { customerAccessToken: string }> = parse(
      gql`
        query ($customerAccessToken: String!) {
           customer(customerAccessToken: $customerAccessToken) {
              orders(first: 100, sortKey: PROCESSED_AT, reverse: true) {
                 nodes {
                    totalPriceV2 {
                       amount
                       currencyCode
                    }
                    statusUrl
                    fulfillmentStatus
                    name
                    id
                    orderNumber
                    successfulFulfillments {
                       trackingCompany
                       trackingInfo {
                          number
                          url
                       }
                    }
                    processedAt
                 }
              }
           }
        }
      `
   );
   const variables = { customerAccessToken: customerAccessToken };
   const response = await graphQLClient.request(query, variables);
   return response.customer.orders.nodes;
}

const fulfillmentStatusMapping: { [key: string]: string } = {
   FULFILLED: _js('Fulfilled'),
   IN_PROGRESS: _js('In Progress'),
   ON_HOLD: _js('On Hold'),
   OPEN: _js('Open'),
   PARTIALLY_FULFILLED: _js('Partially Fulfilled'),
   PENDING_FULFILLMENT: _js('Pending Fulfillment'),
   RESTOCKED: _js('Restocked'),
   SCHEDULED: _js('Scheduled'),
   UNFULFILLED: _js('Unfulfilled'),
};

export function getLocalizedFulfillmentStatus(status: string) {
   return fulfillmentStatusMapping[status];
}
